import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Trans, useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { ApiError, apiFetch } from '../lib/api'
import { PRODUCTS, type EndUser, type EndUserPage, type Product, type ProductQuota } from '../lib/types'
import {
  baselineChanged,
  defaultBody,
  defaultChanged,
  describeChange,
  formatList,
  inForceLimit,
  initialLimitBody,
  isPendingId,
  isRowDirty,
  noUserChanges,
  pendingEndUser,
  pendingId,
  registrationChanged,
  savedDefaults,
  sortListed,
  usersChanged,
  type BaselineDraft,
  type DefaultsDraft,
  type PendingAdd,
  type SortKey,
  type SortOrder,
  type UsersDraft,
  type UsersDrafts,
  withRowDraft,
} from '../lib/userLimits'
import { toast } from '../lib/toast'
import { useProductTab } from '../hooks/useProductTab'
import { useProductStatus } from '../hooks/useProductStatus'
import { useUnsavedGuard } from '../hooks/useUnsavedGuard'
import { useWriteErrorHandler } from '../hooks/useWriteErrorHandler'
import { Tabs } from '../components/ui/Tabs'
import { Card, CardHeader, CardBody } from '../components/ui/Card'
import { Toggle } from '../components/ui/Toggle'
import { QtyStepper } from '../components/ui/QtyStepper'
import { InfoTip } from '../components/ui/InfoTip'
import { SubHead } from '../components/ui/SubHead'
import { Button } from '../components/ui/Button'
import { FilterLabel } from '../components/ui/FilterLabel'
import { Select } from '../components/ui/Select'
import { PageScrollArea, PageView } from '../components/ui/PageScrollArea'
import { PageSaveDiscard } from '../components/ui/PageSaveDiscard'
import { PageHeader } from '../components/ui/PageHeader'
import { QueryState } from '../components/ui/QueryStateCard'
import { WarningIcon } from '../components/ui/WarningIcon'
import { ProductViewGate } from '../components/ProductViewGate'
import { ConfirmChangeModal } from '../components/ConfirmChangeModal'
import { LimitRow } from '../components/LimitRow'
import { AddUserModal } from '../components/AddUserModal'
import { AdminConfirmModal } from '../components/AdminConfirmModal'

/** The setting a `quota.stale_write` refusal was about, so the confirm names
 * the one that moved (`confirm.staleRegistration` / `confirm.staleWrite`). */
type StaleSetting = 'registration' | 'default'

class StaleSettingError extends Error {
  constructor(readonly setting: StaleSetting) {
    super(`stale ${setting} write`)
    this.name = 'StaleSettingError'
  }
}

const MCP: Product = 'Mambu MCP'

const rowKey = (product: Product, id: string) => `${product}|${id}`

/** The default-limit sentence of the "Confirm change" modal, or null when
 * the default limit does not change. */
function DefaultChangeSentence({
  product,
  saved,
  draft,
}: Readonly<{ product: Product; saved: BaselineDraft; draft: BaselineDraft }>) {
  const { t, i18n } = useTranslation()
  const change = describeChange(saved, draft)
  const strong = { strong: <strong /> }
  switch (change.kind) {
    case 'enabled':
      return <Trans i18nKey="userLimits.confirm.enabled" components={strong} />
    case 'disabled':
      return (
        <Trans
          i18nKey="userLimits.confirm.disabled"
          values={{ unit: product === MCP ? t('common.words.invocations') : t('common.words.agentsCredits') }}
          components={strong}
        />
      )
    case 'changed':
      return (
        <Trans
          i18nKey={change.value === 0 ? 'userLimits.confirm.changedZero' : 'userLimits.confirm.changed'}
          values={{ value: change.value.toLocaleString(i18n.language) }}
          components={strong}
        />
      )
    default:
      return null
  }
}

/** The "Confirm change" message for one product (`baselineConfirmMessage`):
 * the registration sentence first, matching the order of the settings on the
 * card, then the default limit's; both moved, they are listed under "Two
 * settings will change" rather than run together. */
function ChangeMessage({
  product,
  saved,
  draft,
}: Readonly<{ product: Product; saved: BaselineDraft; draft: BaselineDraft }>) {
  const { t } = useTranslation()
  const strong = { strong: <strong /> }
  const registration = registrationChanged(saved, draft) ? (
    <Trans
      i18nKey={draft.autoRegister ? 'userLimits.confirm.registrationOn' : 'userLimits.confirm.registrationOff'}
      values={{ product }}
      components={strong}
    />
  ) : null
  const limit = defaultChanged(saved, draft) ? <DefaultChangeSentence product={product} saved={saved} draft={draft} /> : null
  if (registration && limit) {
    return (
      <>
        <Trans i18nKey="userLimits.confirm.twoChanges" components={strong} />
        <ul className="mt-2 pl-[18px] list-disc">
          <li className="mb-1">{registration}</li>
          <li className="mb-1">{limit}</li>
        </ul>
      </>
    )
  }
  return registration ?? limit ?? <>{t('userLimits.confirm.noChange', { product })}</>
}

// The prototype's `section-limits` (`initUserLimits`, subscription-portal.html):
// the page note, the Account Defaults card ("New users", then "Default
// limit"), and the Individual User Limits card, which fills at least the rest
// of the viewport and grows to show ten users before its list scrolls inside
// it; on a small screen the page scrolls to reveal the rest of the card.
//
// The Account Defaults — the "New users" switch and the default limit — and
// the users added or deleted are a page draft for both products, committed by
// the page Save behind one "Confirm change" per product changed and discarded
// by the page Discard (`setDirty`/`snapshot`/`restore`; the prototype's Add
// user and Delete only change the page's map and `markDirty()`). Each saved
// user's row still saves on its own (`.lc-save-edit-btn`). Leaving with any
// unsaved change goes through the shared guard (OQ7).
// ARCHITECTURE_DECISIONS.md §46, §53.
export function UserLimits() {
  const { t, i18n } = useTranslation()
  const { product, setProduct } = useProductTab('user-limits')
  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('pct')
  const [order, setOrder] = useState<SortOrder>('desc')
  const [draft, setDraft] = useState<DefaultsDraft | null>(null)
  const [rowDrafts, setRowDrafts] = useState<Record<string, string>>({})
  // One row's unsaved field text (`undefined` clears it), keyed by rowKey.
  const setRowDraft = (key: string, raw: string | undefined) =>
    setRowDrafts((drafts) => withRowDraft(drafts, key, raw))
  // Users added or deleted, per product, until the page Save writes them.
  const [userDrafts, setUserDrafts] = useState<UsersDrafts>(noUserChanges)
  const [confirmQueue, setConfirmQueue] = useState<Product[]>([])
  // Which write a stale confirm was refused on (OQ15), for its note.
  const [staleNote, setStaleNote] = useState<StaleSetting | null>(null)
  const [committing, setCommitting] = useState(false)
  const [adding, setAdding] = useState(false)
  const [deleting, setDeleting] = useState<EndUser | null>(null)
  const committingRef = useRef(false)
  const savedInRun = useRef(false)
  // Drafted adds the server refused because the user was listed meanwhile.
  const alreadyListed = useRef<string[]>([])
  // Drafted adds the server refused as not an address: kept drafted, flagged.
  const refusedInvalid = useRef<string[]>([])
  const queryClient = useQueryClient()
  const onWriteError = useWriteErrorHandler()

  const status = useProductStatus(product)
  const { quota } = status
  const isFull = (p: Product) => status.view('user-limits', p) === 'full'
  // User Limits is the full page on Contracted and Trial; Expired and Not
  // Contracted are the preview card, so their user list is never fetched.
  const endUsers = useQuery({
    queryKey: ['end-users', product, 'all'],
    queryFn: () =>
      apiFetch<EndUserPage>(`/api/v1/end-users?product=${encodeURIComponent(product)}&pageSize=200`),
    enabled: isFull(product),
  })

  // One draft per page load, seeded once quota arrives (the prototype's
  // `snapshot()` at init).
  useEffect(() => {
    if (quota && !draft) setDraft(savedDefaults(quota))
  }, [quota, draft])

  const saved = quota ? savedDefaults(quota) : null
  const changedProducts = (q: readonly ProductQuota[], d: DefaultsDraft) => {
    const s = savedDefaults(q)
    return PRODUCTS.filter((p) => isFull(p) && (baselineChanged(s[p], d[p]) || usersChanged(userDrafts[p])))
  }
  const dirty = !!quota && !!draft && changedProducts(quota, draft).length > 0

  const word = t('userLimits.defaultWord')
  const list = endUsers.data?.items
  const usersDraft = userDrafts[product]
  const draftHere = draft?.[product]
  // The list as drafted: the saved users less the ones deleted, then the ones
  // added. A drafted add the list now holds (written by this page's Save, or
  // by someone else) is not drawn twice.
  const listed = useMemo(() => {
    if (!list) return undefined
    const kept = list.filter((u) => !usersDraft.deletes.includes(u.id))
    const emails = new Set(kept.map((u) => u.email.toLowerCase()))
    const inForce = inForceLimit(draftHere)
    const added = usersDraft.adds
      .filter((a) => !emails.has(a.email))
      .map((a) => pendingEndUser(a, product, inForce))
    return [...kept, ...added]
  }, [list, usersDraft, draftHere, product])
  const rowsDirty = (listed ?? []).some((u) => isRowDirty(u, rowDrafts[rowKey(product, u.id)], word))

  // A drafted add whose user the reloaded list now holds (another admin
  // added them meanwhile) would otherwise stay drafted with no row to show
  // for it, keeping the page dirty: drop it, with its typed limit, and name
  // the user. Not while a Save runs — its own adds land in the list before
  // the Save forgets them.
  useEffect(() => {
    if (!list || committing) return
    const held = new Set(list.filter((u) => !usersDraft.deletes.includes(u.id)).map((u) => u.email.toLowerCase()))
    const gone = usersDraft.adds.filter((a) => held.has(a.email)).map((a) => a.email)
    if (!gone.length) return
    setUserDrafts((drafts) => ({
      ...drafts,
      [product]: { ...drafts[product], adds: drafts[product].adds.filter((a) => !gone.includes(a.email)) },
    }))
    setRowDrafts((drafts) => {
      let next = drafts
      gone.forEach((email) => (next = withRowDraft(next, rowKey(product, pendingId(email)), undefined)))
      return next
    })
    toast(t('userLimits.addUserModal.emailExists', { email: formatList(gone, i18n.language), count: gone.length }))
  }, [list, usersDraft, committing, product, t, i18n.language])

  function resetDraft() {
    if (quota) setDraft(savedDefaults(quota))
  }
  function dropUserDrafts() {
    setUserDrafts(noUserChanges())
    // A drafted user's typed limit goes with them.
    setRowDrafts((drafts) =>
      Object.fromEntries(Object.entries(drafts).filter(([key]) => !isPendingId(key.split('|')[1] ?? '')))
    )
  }
  useUnsavedGuard(dirty || rowsDirty, () => {
    resetDraft()
    setUserDrafts(noUserChanges())
    setRowDrafts({})
  })

  /** Discard's question names what it drops: the Account Defaults draft, the
   * users added or deleted (on either product), or both. */
  function discardQuestion(): string {
    const users = PRODUCTS.some((p) => usersChanged(userDrafts[p]))
    if (!users) return t('userLimits.discardDefaultsConfirm')
    const defaults = !!saved && !!draft && PRODUCTS.some((p) => baselineChanged(saved[p], draft[p]))
    return t(defaults ? 'userLimits.discardAllConfirm' : 'userLimits.discardUsersConfirm')
  }

  function discard() {
    if (!window.confirm(discardQuestion())) return
    resetDraft()
    dropUserDrafts()
  }

  function setUsersDraft(p: Product, next: (d: UsersDraft) => UsersDraft) {
    setUserDrafts((drafts) => ({ ...drafts, [p]: next(drafts[p]) }))
  }

  function draftAdd(add: PendingAdd) {
    setUsersDraft(product, (d) => ({ ...d, adds: [...d.adds, add] }))
  }

  // A row's Delete, once confirmed: a drafted user is simply dropped; a saved
  // one is hidden until the Save deletes them (`delete t.getMap()[email]` and
  // `markDirty()`). Their typed limit is kept, so Discard brings the row back
  // as it was.
  function draftDelete(user: EndUser) {
    if (isPendingId(user.id)) {
      setUsersDraft(product, (d) => ({ ...d, adds: d.adds.filter((a) => pendingId(a.email) !== user.id) }))
      setRowDraft(rowKey(product, user.id), undefined)
    } else {
      setUsersDraft(product, (d) => ({ ...d, deletes: [...d.deletes, user.id] }))
    }
    setDeleting(null)
  }

  // A drafted user's own Save or Reset changes the limit they will be added
  // on; nothing is written until the page Save.
  function redraftAdd(user: EndUser, limit: number | null) {
    setUsersDraft(product, (d) => ({
      ...d,
      adds: d.adds.map((a) => (pendingId(a.email) === user.id ? { ...a, limit } : a)),
    }))
    setRowDraft(rowKey(product, user.id), undefined)
  }

  /** Forget the user changes the server has taken, with their typed limits;
   * whatever was not written stays drafted. */
  function pruneUserDrafts(p: Product, added: ReadonlySet<string>, deleted: ReadonlySet<string>) {
    if (!added.size && !deleted.size) return
    setUsersDraft(p, (d) => ({
      adds: d.adds.filter((a) => !added.has(a.email)),
      deletes: d.deletes.filter((id) => !deleted.has(id)),
    }))
    setRowDrafts((drafts) => {
      let next = drafts
      added.forEach((email) => (next = withRowDraft(next, rowKey(p, pendingId(email)), undefined)))
      deleted.forEach((id) => (next = withRowDraft(next, rowKey(p, id), undefined)))
      return next
    })
  }

  function setProductDraft(next: Partial<BaselineDraft>) {
    setDraft((d) => (d ? { ...d, [product]: { ...d[product], ...next } } : d))
  }

  function startSave() {
    if (!quota || !draft) return
    const changed = changedProducts(quota, draft)
    if (!changed.length) return
    savedInRun.current = false
    alreadyListed.current = []
    refusedInvalid.current = []
    setStaleNote(null)
    setConfirmQueue(changed)
  }

  /** The run's one toast: that it saved, followed by the drafted users it
   * could not add — listed meanwhile by someone else, or refused as not an
   * address (those stay drafted, their rows flagged). */
  function finishRun() {
    const notes = [
      alreadyListed.current.length
        ? t('userLimits.addUserModal.emailExists', {
            email: formatList(alreadyListed.current, i18n.language),
            count: alreadyListed.current.length,
          })
        : null,
      refusedInvalid.current.length
        ? t('userLimits.addInvalidNote', {
            emails: formatList(refusedInvalid.current, i18n.language),
            count: refusedInvalid.current.length,
          })
        : null,
    ]
      .filter((note) => note !== null)
      .join(' ')
    const savedSome = savedInRun.current
    alreadyListed.current = []
    refusedInvalid.current = []
    savedInRun.current = false
    if (savedSome) toast(notes ? t('userLimits.savedWithNotes', { notes }) : t('userLimits.defaultsSaved'))
    else if (notes) toast(notes)
  }

  function advance() {
    setStaleNote(null)
    if (confirmQueue.length <= 1) finishRun()
    setConfirmQueue((q) => q.slice(1))
  }

  // OQ15: someone else changed the defaults since this page read them.
  // Reload, keep the admin's draft, and ask again against the fresh values,
  // with a note naming the setting that moved — unless the reload shows
  // there is nothing left to change. Drafted users still to write keep the
  // confirm open without the note: they were not what was refused.
  async function reconfirmAfterStaleWrite(p: Product, refused: StaleSetting) {
    await queryClient.invalidateQueries({ queryKey: ['quota'] })
    const fresh = queryClient.getQueryData<ProductQuota[]>(['quota'])
    if (fresh && draft && baselineChanged(savedDefaults(fresh)[p], draft[p])) setStaleNote(refused)
    else if (usersChanged(userDrafts[p])) setStaleNote(null)
    else advance()
  }

  async function refreshProduct(p: Product) {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['quota'] }),
      // Switching the default off gives its users their own limit; adds and
      // deletes change the list.
      queryClient.invalidateQueries({ queryKey: ['end-users', p, 'all'] }),
    ])
  }

  /** One settings write. A `quota.stale_write` refusal is re-thrown as a
   * `StaleSettingError` naming the setting, so the confirm can say which one
   * moved; anything else propagates as it is. */
  async function writeSetting(setting: StaleSetting, path: string, body: unknown) {
    try {
      await apiFetch(path, { method: 'PUT', body: JSON.stringify(body) })
    } catch (error) {
      if (error instanceof ApiError && error.code === 'quota.stale_write') throw new StaleSettingError(setting)
      throw error
    }
    savedInRun.current = true
  }

  /** Registration first, the order of the settings on the card. Each write
   * carries the value the confirm was drawn from, so a change made meanwhile
   * by someone else is refused as stale (OQ15). */
  async function writeSettings(p: Product, base: BaselineDraft, next: BaselineDraft) {
    const q = `/api/v1/quota/${encodeURIComponent(p)}`
    if (registrationChanged(base, next)) {
      await writeSetting('registration', `${q}/registration`, {
        autoRegisterUsers: next.autoRegister,
        expectedAutoRegisterUsers: base.autoRegister,
      })
    }
    if (defaultChanged(base, next)) {
      await writeSetting('default', `${q}/default-limit`, {
        defaultUserLimit: defaultBody(next),
        expectedDefaultUserLimit: base.value,
        expectedDefaultUserLimitEnabled: base.enabled,
      })
    }
  }

  async function deleteDrafted(p: Product, id: string) {
    try {
      await apiFetch(`/api/v1/end-users/${id}/limit?product=${encodeURIComponent(p)}`, { method: 'DELETE' })
    } catch (error) {
      // Already gone (someone else deleted them): the delete stands.
      if (!(error instanceof ApiError && error.status === 404)) throw error
    }
    savedInRun.current = true
  }

  /** One drafted add; true when it is done with — written, or listed
   * meanwhile — and false when it stays drafted. */
  async function addDrafted(p: Product, add: PendingAdd): Promise<boolean> {
    try {
      await apiFetch('/api/v1/end-users', {
        method: 'POST',
        body: JSON.stringify({ email: add.email, action: 'add', product: p, initialLimit: initialLimitBody(add) }),
      })
      savedInRun.current = true
      return true
    } catch (error) {
      if (!(error instanceof ApiError)) throw error
      // Listed meanwhile by someone else: the reload shows them with the
      // limit they have; the toast names them.
      if (error.code === 'end_user_already_exists') {
        alreadyListed.current.push(add.email)
        return true
      }
      // This address alone is refused: flag its row and go on with the
      // rest. Anything page-wide (a status conflict, a 5xx) stops the run.
      if (error.code === 'end_user_invalid_email') {
        refusedInvalid.current.push(add.email)
        setUsersDraft(p, (d) => ({
          ...d,
          adds: d.adds.map((a) => (a.email === add.email ? { ...a, refused: 'invalidEmail' } : a)),
        }))
        return false
      }
      throw error
    }
  }

  /** The users, after the defaults so a user added on the default starts on
   * the one just saved — a default switched off as drafted leaves them with
   * no limit, as the Add modal said — and deletes before adds, so a user
   * deleted and added again in one draft is re-added. `added`/`deleted`
   * collect what landed, so a failure part-way leaves only the rest drafted. */
  async function writeUsers(p: Product, users: UsersDraft, added: Set<string>, deleted: Set<string>) {
    for (const id of users.deletes) {
      await deleteDrafted(p, id)
      deleted.add(id)
    }
    for (const add of users.adds) {
      if (await addDrafted(p, add)) added.add(add.email)
    }
  }

  async function commitOne(p: Product) {
    if (!draft || !saved || committingRef.current) return
    committingRef.current = true
    setCommitting(true)
    const added = new Set<string>()
    const deleted = new Set<string>()
    try {
      await writeSettings(p, saved[p], draft[p])
      await writeUsers(p, userDrafts[p], added, deleted)
      await refreshProduct(p)
      pruneUserDrafts(p, added, deleted)
      advance()
    } catch (error) {
      if (error instanceof StaleSettingError) {
        await reconfirmAfterStaleWrite(p, error.setting)
      } else {
        // Stop the queue: the rest were confirmed against the same view.
        setConfirmQueue([])
        // A write in this run already landed (the registration switch ahead
        // of a refused default-limit write, an earlier product, or some of
        // the users): reload, so the page's saved values match the server
        // and the next Save is not refused as stale. What was not written —
        // and every typed limit with it — stays drafted for another Save.
        if (savedInRun.current || alreadyListed.current.length) await refreshProduct(p)
        pruneUserDrafts(p, added, deleted)
        finishRun()
        await onWriteError(error)
      }
    } finally {
      committingRef.current = false
      setCommitting(false)
    }
  }

  async function saveUserLimit(user: EndUser, value: number | null, reset: boolean) {
    // `product` is required: the id is the registration, and the limit hangs
    // off (registration, product).
    try {
      await apiFetch(`/api/v1/end-users/${user.id}/limit?product=${encodeURIComponent(product)}`, {
        method: 'PUT',
        body: JSON.stringify({ userLimit: value }),
      })
    } catch (error) {
      await onWriteError(error)
      return
    }
    toast(t(reset ? 'userLimits.resetDone' : 'userLimits.saved'))
    setRowDraft(rowKey(product, user.id), undefined)
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['quota'] }),
      queryClient.invalidateQueries({ queryKey: ['end-users', product, 'all'] }),
    ])
  }

  // Sorted on committed data, so typing in the default never reorders rows:
  // a drafted user sorts as if on the saved default (T-LIM-06).
  const sortBasis = inForceLimit(saved?.[product])
  const users = useMemo(() => {
    const q = search.trim().toLowerCase()
    const shown = (listed ?? []).filter((u) => !q || u.email.toLowerCase().includes(q))
    return sortListed(shown, usersDraft.adds, sortBasis, sortKey, order)
  }, [listed, usersDraft, sortBasis, search, sortKey, order])

  const confirming = confirmQueue[0]

  return (
    <PageView>
      <PageHeader
        title={t('userLimits.title')}
        subtitle={t('userLimits.subtitle')}
        actions={<PageSaveDiscard dirty={dirty} onSave={startSave} onDiscard={discard} />}
      />

      <Tabs value={product} onChange={setProduct} options={PRODUCTS} />

      {/* `#section-limits .tab-scroll-area` is a column, so the Individual
          card can take at least the height left below the defaults. */}
      <PageScrollArea className="flex flex-col">
        <ProductViewGate status={status} page="user-limits" product={product}>
          {(_view, current) => {
            const savedDefault = savedDefaults([current])[product]
            const d = draft?.[product] ?? savedDefault
            // What a user without their own limit runs at, as drafted: the
            // drafted default when on; while it is drafted off but still on,
            // the figure Save would give them (`toggle` change, §16j).
            const defaultPreview = d.enabled ? (d.value ?? 0) : current.defaultUserLimit
            const unit = product === MCP ? t('common.words.invocations') : t('common.words.agentsCredits')
            return (
              <div className="flex-[1_0_auto] flex flex-col gap-4">
                <div className="flex items-center gap-2.5 min-h-[44px] border border-[rgba(255,193,7,.45)] bg-[rgba(255,193,7,.12)] rounded-lg px-3.5 py-2.5 text-[12.5px] text-[var(--text-secondary)] leading-relaxed">
                  <WarningIcon size={16} stroke="#e0a800" className="shrink-0" />
                  <div>
                    <Trans
                      i18nKey="userLimits.pageCallout.combined"
                      values={{ product, unit }}
                      components={{ strong: <strong /> }}
                    />
                  </div>
                </div>

                <Card collapsible>
                  <CardHeader title={t('userLimits.default.title')} subtitle={t('userLimits.default.subtitle')} />
                  <CardBody>
                    {/* "New users" (MS-996): automatic registration on first
                        usage, or only the users listed are served. A page
                        draft, saved with the default limit by the page Save. */}
                    <SubHead label={t('userLimits.registration.sectionLabel')} className="mb-2" />
                    <div className="flex items-center gap-3">
                      <span className="flex flex-1 items-center gap-2 text-[13px] text-[var(--text-primary)]">
                        {t('userLimits.registration.autoLabel')}
                        <InfoTip text={t('userLimits.registration.tooltip')} placement="below" align="left" width={330} />
                      </span>
                      <Toggle
                        checked={d.autoRegister}
                        ariaLabel={t('userLimits.registration.autoLabel')}
                        onChange={(autoRegister) => setProductDraft({ autoRegister })}
                      />
                    </div>
                    <div className="border-t border-[var(--border-light)] my-3.5" />
                    <SubHead label={t('userLimits.default.sectionLabel')} className="mb-2" />
                    <div className="flex items-center gap-3">
                      <span className="flex flex-1 items-center gap-2 text-[13px] text-[var(--text-primary)]">
                        {t(product === MCP ? 'userLimits.default.enforceMcp' : 'userLimits.default.enforceAgt')}
                        <InfoTip text={t('userLimits.default.enforceTooltip')} placement="below" align="left" width={300} />
                      </span>
                      <QtyStepper
                        value={d.value}
                        min={0}
                        editable
                        disabled={!d.enabled}
                        placeholder={t('userLimits.default.placeholder')}
                        label={t('userLimits.default.sectionLabel')}
                        onChange={(value) => setProductDraft({ value })}
                      />
                      <Toggle
                        checked={d.enabled}
                        ariaLabel={t(product === MCP ? 'userLimits.default.enforceMcp' : 'userLimits.default.enforceAgt')}
                        // On: the kept figure, or 0 when none was ever set (OQ2).
                        onChange={(enabled) => setProductDraft({ enabled, value: d.value ?? (enabled ? 0 : null) })}
                      />
                    </div>
                  </CardBody>
                </Card>

                {/* `.limits-card-fill`: at least the height left in the
                    viewport, growing past it so the list shows ten users
                    before it scrolls; collapsed, it shrinks to its header. */}
                <Card
                  collapsible
                  className="flex-[1_0_auto] flex flex-col data-[collapsed=true]:flex-none data-[collapsed=true]:min-h-0"
                >
                  <CardHeader
                    title={t('userLimits.individual.title')}
                    subtitle={t('userLimits.individual.subtitle')}
                    actions={
                      // Available in both registration modes (MS-996): with
                      // automatic registration on, an admin can still add a
                      // user ahead of their first usage.
                      <Button
                        variant="ghost"
                        title={t('userLimits.addUserTooltip')}
                        onClick={() => setAdding(true)}
                        className="!text-[12px] !px-3 !py-[5px]"
                      >
                        {t('userLimits.addUser')}
                      </Button>
                    }
                  />
                  <CardBody className="flex-[1_0_auto] flex flex-col">
                    <div className="flex gap-3 flex-wrap items-end mb-6">
                      <div className="flex-[1_1_220px] min-w-[200px] flex flex-col gap-1">
                        <FilterLabel
                          label={t('userLimits.individual.searchLabel')}
                          onClear={search ? () => setSearch('') : undefined}
                        />
                        <input
                          type="text"
                          placeholder={t('userLimits.individual.searchPlaceholder')}
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          className="border border-[var(--border)] rounded-md px-3 py-1.5 text-[13px] bg-[var(--bg-input)] text-[var(--text-primary)]"
                        />
                      </div>
                      <div className="flex-none w-[150px] flex flex-col gap-1">
                        <FilterLabel
                          label={t('userLimits.individual.sortByLabel')}
                          onClear={sortKey === 'pct' ? undefined : () => setSortKey('pct')}
                        />
                        <Select
                          aria-label={t('userLimits.individual.sortByLabel')}
                          value={sortKey}
                          onChange={(e) => setSortKey(e.target.value as SortKey)}
                        >
                          <option value="pct">{t('userLimits.individual.sort.pct')}</option>
                          <option value="username">{t('userLimits.individual.sort.username')}</option>
                          <option value="email">{t('userLimits.individual.sort.email')}</option>
                          <option value="limit">{t('userLimits.individual.sort.limit')}</option>
                        </Select>
                      </div>
                      <div className="flex-none w-[150px] flex flex-col gap-1">
                        <FilterLabel
                          label={t('userLimits.individual.orderLabel')}
                          onClear={order === 'desc' ? undefined : () => setOrder('desc')}
                        />
                        <Select
                          aria-label={t('userLimits.individual.orderLabel')}
                          value={order}
                          onChange={(e) => setOrder(e.target.value as SortOrder)}
                        >
                          <option value="desc">{t('userLimits.individual.order.desc')}</option>
                          <option value="asc">{t('userLimits.individual.order.asc')}</option>
                        </Select>
                      </div>
                    </div>

                    {/* `.limits-scroll`: ten 64px rows plus nine 10px gaps,
                        then it scrolls inside the card, its scrollbar pulled
                        18px into the card's right padding so it sits near the
                        border rather than over the row controls; the padding
                        gives back the scrollbar's width (`--sbw`) so the rows
                        still line up with the filters above. */}
                    <div
                      data-testid="limits-scroll"
                      className="max-h-[730px] overflow-y-auto -mr-[18px] pr-[calc(18px-var(--sbw,0px))] flex flex-col gap-2.5"
                    >
                      {endUsers.isError && <QueryState state="error" onRetry={() => endUsers.refetch()} />}
                      {!endUsers.isError && !listed && <QueryState state="pending" />}
                      {listed && users.length === 0 && (
                        <div className="p-5 text-center text-[13px] text-[var(--text-tertiary)]">
                          {t('userLimits.individual.empty')}
                        </div>
                      )}
                      {users.map((u) => {
                        const key = rowKey(product, u.id)
                        const drafted = isPendingId(u.id)
                        const refused = drafted && usersDraft.adds.some((a) => pendingId(a.email) === u.id && a.refused)
                        return (
                          <LimitRow
                            key={key}
                            user={u}
                            raw={rowDrafts[key]}
                            onRawChange={(raw) => setRowDraft(key, raw)}
                            onDefault={u.userLimit === null && d.enabled}
                            defaultEnabled={d.enabled}
                            // A drafted user's Reset writes nothing, so it may
                            // put them on a default only drafted on.
                            savedDefaultEnabled={drafted ? d.enabled : savedDefault.enabled}
                            defaultPreview={defaultPreview}
                            onSave={(limit, reset) =>
                              drafted ? redraftAdd(u, limit) : saveUserLimit(u, limit, reset)
                            }
                            onDelete={() => setDeleting(u)}
                            error={refused ? t('userLimits.addUserModal.emailInvalid') : undefined}
                          />
                        )
                      })}
                    </div>
                  </CardBody>
                </Card>
              </div>
            )
          }}
        </ProductViewGate>
      </PageScrollArea>

      {confirming && draft && saved && (
        <ConfirmChangeModal
          key={confirming}
          subtitle={confirming}
          message={<ChangeMessage product={confirming} saved={saved[confirming]} draft={draft[confirming]} />}
          note={staleNoteText(t, staleNote)}
          pending={committing}
          onConfirm={() => commitOne(confirming)}
          onCancel={() => {
            // Cancel keeps the page dirty (`tenantWideConfirm` just closes).
            setConfirmQueue([])
            setStaleNote(null)
            finishRun()
          }}
        />
      )}

      {adding && draftHere && (
        <AddUserModal
          key={product}
          defaultEnforced={draftHere.enabled}
          existingEmails={(listed ?? []).map((u) => u.email)}
          onAdd={draftAdd}
          onClose={() => setAdding(false)}
        />
      )}

      {deleting && draftHere && (
        <DeleteUserModal
          email={deleting.email}
          autoRegister={draftHere.autoRegister}
          onConfirm={() => draftDelete(deleting)}
          onCancel={() => setDeleting(null)}
        />
      )}
    </PageView>
  )
}

/** The note on a confirm asked again after a stale write (OQ15), naming the
 * setting someone else changed. */
function staleNoteText(t: TFunction, refused: StaleSetting | null): string | undefined {
  if (refused === 'registration') return t('userLimits.confirm.staleRegistration')
  return refused === 'default' ? t('userLimits.confirm.staleWrite') : undefined
}

/** A row's Delete confirmation — the prototype's `showAdminConfirm` call on
 * `.lc-delete-btn`, with its default subtitle, summary rows and checkbox. What
 * it promises follows the tab's registration switch as drafted (the
 * prototype's live `t.getOnb()`): with automatic registration on the user is
 * registered again on their next call; off, they are refused. Confirming
 * drafts the delete; the page Save writes it. */
function DeleteUserModal({
  email,
  autoRegister,
  onConfirm,
  onCancel,
}: Readonly<{
  email: string
  autoRegister: boolean
  onConfirm: () => void
  onCancel: () => void
}>) {
  const { t } = useTranslation()
  const mode = autoRegister ? 'auto' : 'manual'
  return (
    <AdminConfirmModal
      title={t('userLimits.deleteConfirm.title', { email })}
      subtitle={t('userLimits.deleteConfirm.subtitle')}
      impactTitle={t(`userLimits.deleteConfirm.impactTitle.${mode}`)}
      impactText={t(`userLimits.deleteConfirm.impactText.${mode}`, { email })}
      bullets={[
        [t('userLimits.deleteConfirm.scopeLabel'), t('userLimits.deleteConfirm.scopeValue')],
        [t('userLimits.deleteConfirm.auditLabel'), t('userLimits.deleteConfirm.auditValue')],
      ]}
      checkLabel={t('userLimits.deleteConfirm.checkLabel')}
      confirmLabel={t('userLimits.deleteUser')}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  )
}
